import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_PAGE_NUMBER } from '@components/listing-temoignages/config';
import { ListingTemoignages } from '@components/listing-temoignages/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { Testimony } from '@shared/services/elastic-search/models';
import { LogsService } from '@shared/services/logs';
import { UrlParamsService } from '@shared/services/url-params';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-listing-temoignages',
  templateUrl: './listing-temoignages.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, ImageComponent, SvgComponent, ButtonComponent, TranslatePipe, TitleComponent],
})
export class ListingTemoignagesComponent extends AbstractDynamicComponent<ListingTemoignages> {
  protected readonly testimonies = signal<Testimony[]>([]);
  protected readonly hasMoreTestimonies = signal<boolean>(false);
  protected readonly isLoading = signal<boolean>(false);
  private readonly urlParamsService = inject(UrlParamsService);
  private readonly logs = inject(LogsService);
  private readonly currentPageFromUrl = computed(() =>
    Number(this.urlParamsService.param('temoignage_page')() ?? DEFAULT_PAGE_NUMBER)
  );
  private readonly elasticSearchService = inject(ElasticSearchService);
  private firstInit = true;
  private itemsPerPage = DEFAULT_ITEMS_PER_PAGE;
  private totalItems = 0;
  private remainingItems = 0;
  private nbTestimoniesLoaded = 0;
  private isAllTestimoniesLoaded = false;
  private previousUrl: string = '';

  constructor() {
    super();
    effect(() => {
      const currentUrl = this.urlParamsService.currentUrl()();
      if (currentUrl !== this.previousUrl) {
        this.previousUrl = currentUrl;
        this.firstInit = true;
        this.totalItems = 0;
        this.remainingItems = 0;
        this.nbTestimoniesLoaded = 0;
        this.isAllTestimoniesLoaded = false;
      }

      const currentPage = this.currentPageFromUrl() ?? 1;
      const itemsToFetch = this.firstInit ? currentPage * DEFAULT_ITEMS_PER_PAGE : DEFAULT_ITEMS_PER_PAGE;
      const itemFrom = this.nbTestimoniesLoaded;
      this.itemsPerPage = itemsToFetch;

      if (!this.firstInit && this.totalItems !== 0 && this.remainingItems <= 0) return;

      this.isLoading.set(true);
      this.elasticSearchService
        .getTestimonies(itemFrom, this.itemsPerPage)
        .pipe(
          take(1),
          tap((list) => {
            if (this.totalItems === 0) {
              this.totalItems = list.totalItems;
            }
            if (itemFrom === 0) {
              untracked(() => this.testimonies.set(list.items));
            } else {
              untracked(() => this.testimonies.update((testimonies) => [...testimonies, ...list.items]));
            }
            this.firstInit = false;
            this.nbTestimoniesLoaded = untracked(() => this.testimonies().length);
            this.remainingItems = this.totalItems - this.nbTestimoniesLoaded;

            if (this.remainingItems <= 0) {
              this.isAllTestimoniesLoaded = true;
              this.hasMoreTestimonies.set(false);
              this.logs.info(
                'ListingTemoignagesComponent',
                'All testimonies have been loaded',
                this.nbTestimoniesLoaded
              );
            } else {
              this.hasMoreTestimonies.set(true);
            }
            this.isLoading.set(false);
          })
        )
        .subscribe({
          error: (err) => {
            this.logs.error('ListingTemoignagesComponent', 'getTestimonies failed', err);
            this.isLoading.set(false);
          },
        });
    });
  }

  loadMore() {
    if (!this.isLoading() && !this.isAllTestimoniesLoaded) {
      this.urlParamsService.mergeParams({ temoignage_page: this.currentPageFromUrl() + 1 });
    }
  }
}
